import { useState, useRef } from 'react';

import { mergeChunks, encodeToWav } from '../../service/AudioService';
import { useEventCallback } from '../useEventCallback/useEventCallback';

interface Options<T = any> {
    recordedChunks: Float32Array[];
    onStart?: () => void;
    onEnd?: (blob: Blob, data?: T) => Promise<void> | void;
}

const defaultOptions: Options = {
    recordedChunks: [],
    onStart: undefined,
    onEnd: undefined,
};

export const useGetPartialRecording = <T = any>(props: Partial<Options<T>>) => {
    const options = { ...defaultOptions, ...props };
    const { recordedChunks, onStart, onEnd } = options;

    const [partialWavBlob, setPartialWavBlob] = useState<Blob | null>(null);
    const [isPartialActive, setIsPartialActive] = useState(false);

    const _onStart = useEventCallback(onStart);
    const _onEnd = useEventCallback(onEnd);

    const partialStartTimeRef = useRef<string | null>(null);
    const partialEndTimeRef = useRef<string | null>(null);


    const partialStartIndexRef = useRef<number>(0);

    const startPartialRecording = () => {
        // check ref instaed of state due to settimeout issues
        if (partialStartIndexRef.current) return;

        partialStartIndexRef.current = recordedChunks.length;

        partialStartTimeRef.current = new Date().toISOString();

        setIsPartialActive(true);
        _onStart?.();
    };

    const stopPartialRecording = (data?: T) => {
        // check ref instaed of state due to settimeout issues
        if (!partialStartIndexRef.current) return;

        if (recordedChunks.length) {
            const partialChunks = recordedChunks.slice(partialStartIndexRef.current);

            const merged = mergeChunks(partialChunks);
            const blob = encodeToWav(merged);

            partialEndTimeRef.current = new Date().toISOString();

            setPartialWavBlob(blob);
           _onEnd?.(blob, {
                promptEndReason: data,   
                recordingStartTimestamp: partialStartTimeRef.current,
                recordingEndTimestamp: partialEndTimeRef.current,           
            } as T);

        }

        partialStartIndexRef.current = 0; // Reset start index
        setIsPartialActive(false);
    };

    return {
        isPartialActive,
        partialWavBlob,
        startPartialRecording,
        stopPartialRecording,
    };
};
